// Global ActivityTracker: records active engagement time on program content pages.
// Singleton; no React state. Uses performance.now() + Date.now() for duration.
// Sessions are queued and sent in batch on route change or unload.

const BATCH_URL = '/api/v1/records/time-tracker/batch/';
const IDLE_TIMEOUT_MS = 60 * 1000; // 60 seconds
const MIN_DURATION_SECONDS = 10;
const MAX_DURATION_SECONDS = 30 * 60; // 30 minutes

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'] as const;

export type ActivityContext = {
  programId: string;
  contentId: string;
  studyGroupId?: string | null;
  trackId?: string | null;
};

type QueuedSession = {
  programId: string;
  contentId: string;
  studyGroupId?: string | null;
  trackId?: string | null;
  durationSeconds: number;
};

let sessionStartTime = 0;
let idleTimeoutId: ReturnType<typeof setTimeout> | null = null;
let sessionStartWallTime = 0;
let currentContext: ActivityContext | null = null;
const queue: QueuedSession[] = [];

function getCsrfToken(): string | null {
  const el = document.getElementById('csrf_token');
  if (!el) return null;
  try {
    return JSON.parse(el.innerHTML) as string;
  } catch {
    return null;
  }
}

function endSession(): void {
  if (currentContext == null || sessionStartTime === 0) return;
  const elapsed = (performance.now() - sessionStartTime) / 1000;
  sessionStartTime = 0;
  if (idleTimeoutId != null) {
    clearTimeout(idleTimeoutId);
    idleTimeoutId = null;
  }
  if (elapsed < MIN_DURATION_SECONDS) return;
  const durationSeconds = Math.min(Math.floor(elapsed), MAX_DURATION_SECONDS);
  queue.push({
    programId: currentContext.programId,
    contentId: currentContext.contentId,
    studyGroupId: currentContext.studyGroupId ?? undefined,
    trackId: currentContext.trackId ?? undefined,
    durationSeconds,
  });
}

function scheduleIdle(): void {
  if (idleTimeoutId != null) clearTimeout(idleTimeoutId);
  idleTimeoutId = setTimeout(() => {
    idleTimeoutId = null;
    endSession();
    currentContext = null;
    removeActivityListeners();
  }, IDLE_TIMEOUT_MS);
}

function onActivity(): void {
  if (currentContext == null) return;
  scheduleIdle();
}

function addActivityListeners(): void {
  ACTIVITY_EVENTS.forEach((name) => {
    document.addEventListener(name, onActivity, { passive: true });
  });
}

function removeActivityListeners(): void {
  ACTIVITY_EVENTS.forEach((name) => {
    document.removeEventListener(name, onActivity);
  });
}

function sendBatch(payload: QueuedSession[], keepalive = false): void {
  if (payload.length === 0) return;
  const body = JSON.stringify({ sessions: payload });
  const token = getCsrfToken();

  if (keepalive) {
    fetch(BATCH_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'X-CSRFToken': token }),
      },
      body,
      keepalive: true,
    }).catch((err) => {
      console.error('TimeTracker batch failed', err);
    });
    return;
  }

  fetch(BATCH_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'X-CSRFToken': token }),
    },
    body,
  }).catch((err) => {
    console.error('TimeTracker batch failed', err);
  });
}

// Start tracking on a content route. Call when user enters /program/:programId/content/:contentId/ 
// with optional query study-group-id, track-id.
export function start(context: ActivityContext): void {
  endSession();
  flush();
  currentContext = context;
  sessionStartTime = performance.now();
  sessionStartWallTime = Date.now();
  scheduleIdle();
  addActivityListeners();
}


// Pause tracking without flushing. Used when tab becomes hidden (visibilitychange).
// Ends current session (pushes to queue), clears context and listeners.
// Queue is sent on next route change or unload. When tab becomes visible again,
// caller should start(context) if still on a content route.
export function pause(): void {
  endSession();
  if (idleTimeoutId != null) {
    clearTimeout(idleTimeoutId);
    idleTimeoutId = null;
  }
  currentContext = null;
  removeActivityListeners();
}

// Stop tracking and flush queued sessions. Called on route change or before unload.
// For page unload, should be called with keepalive=true so the request can outlive the page
export function stop(keepalive = false): void {
  endSession();
  currentContext = null;
  removeActivityListeners();
  if (queue.length > 0) {
    const toSend = queue.splice(0, queue.length);
    sendBatch(toSend, keepalive);
  }
}

// Flush queued sessions to the backend (for example on route change). Doesnt end current session
export function flush(keepalive = false): void {
  if (queue.length === 0) return;
  const toSend = queue.splice(0, queue.length);
  sendBatch(toSend, keepalive);
}

// Parse context from pathname and search.
// Path: /program/:programId/content/:contentId/
// Query: study-group-id, track-id
export function parseContextFromLocation(pathname: string, search: string): ActivityContext | null {
  const match = /^\/program\/([^/]+)\/content\/([^/]+)\/?/.exec(pathname);
  if (!match || match[1] == null || match[2] == null) return null;
  const programId: string = match[1];
  const contentId: string = match[2];
  const params = new URLSearchParams(search);
  const studyGroupId = params.get('study-group-id') || undefined;
  const trackId = params.get('track-id') || undefined;
  return { programId, contentId, studyGroupId: studyGroupId || null, trackId: trackId || null };
}

// Register unload handler to flush with keepalive when user closes tab/navigates away.
export function registerUnloadFlush(): void {
  const flushOnUnload = () => {
    stop(true);
  };
  window.addEventListener('beforeunload', flushOnUnload);
  window.addEventListener('pagehide', flushOnUnload);
}
